import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  ArrowLeft,
  Archive,
  ChevronRight,
  Loader2,
  MapPin,
  Pencil,
  FileSignature,
  Plus,
  RotateCcw,
  Spline,
  Star,
  TreePine,
  Droplet,
  UserRound,
} from 'lucide-react'
import type { ContactDto } from '@lib/dto/contact.dto'
import type { SiteDto } from '@lib/dto/site.dto'
import {
  createContactInputSchema,
  updateContactInputSchema,
} from '@lib/input/contact.input'
import { createSiteInputSchema, updateSiteInputSchema } from '@lib/input/site.input'
import { SITE_MAP_STATUSES, SITE_MAP_STATUS_LABELS } from '@lib/enums/site-map-status.enum'
import { CLIENT_STATUSES, CLIENT_STATUS_LABELS } from '@lib/enums/client-status.enum'
import { CLIENT_TYPES, CLIENT_TYPE_LABELS } from '@lib/enums/client-type.enum'
import type { ClientType } from '@lib/enums/client-type.enum'
import type { ClientDetailDto } from '@lib/dto/client.dto'
import { parseUuid } from '@lib/validators/uuid.validator'
import { updateClientInputSchema } from '@lib/input/client.input'
import type { ClientStatus } from '@lib/enums/client-status.enum'
import type { SiteMapStatus } from '@lib/enums/site-map-status.enum'
import { useClient } from '@/hooks/useClient'
import { useArchiveClient, useUpdateClient } from '@/hooks/useClientMutations'
import { useAddContact, useArchiveContact, useUpdateContact } from '@/hooks/useContactMutations'
import { useAddSite, useArchiveSite, useUpdateSite } from '@/hooks/useSiteMutations'
import { useDocuments } from '@/hooks/useDocuments'
import { useHousecallProStatus } from '@/hooks/useHousecallPro'
import { useProposals } from '@/hooks/useProposals'
import { DocumentTable } from '@/components/DocumentTable'
import { HcpAddressLink } from '@/components/HcpAddressLink'
import { HcpCustomerLink } from '@/components/HcpCustomerLink'
import { HomeCountControl } from '@/components/HomeCountControl'
import { UploadButton } from '@/components/UploadButton'
import { ProposalTable } from '@/pages/ProposalsPage'
import { SiteVisitsCell } from '@/site-visits/SiteVisitsCell'
import { errorMessage } from '@/helpers/logger'
import {
  ClientStatusBadge,
  ClientTypeBadge,
  SiteMapStatusBadge,
} from '@/components/ClientBadges'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  COL_OPTIONAL,
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  ROW_ACTION,
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'

/**
 * Join the parts of an address that are actually set.
 *
 * A street or a city is required for there to be an address at all. A record
 * carrying only its state renders as a lone "TX", which reads as a field that
 * failed to load rather than as a location — showing nothing is more honest
 * than showing a fragment.
 */
type AddressFields = Pick<
  ClientDetailDto,
  'addressLine1' | 'addressLine2' | 'city' | 'state' | 'postalCode'
>

function formatAddress(record: AddressFields): string {
  if (!record.addressLine1?.trim() && !record.city?.trim()) {
    return '—'
  }
  const street = [record.addressLine1, record.addressLine2]
    .filter(Boolean)
    .join(', ')
  const region = [record.city, record.state].filter(Boolean).join(', ')
  const parts = [street, region, record.postalCode].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : '—'
}

/**
 * A client and everything hanging off it: the people to talk to and the places
 * work happens. This is the screen someone opens before writing a proposal, so
 * contacts and sites are both visible at once rather than behind tabs.
 */
export function ClientDetailPage() {
  const params = useParams<{ id: string }>()
  const clientId = parseUuid(params.id)

  const client = useClient(clientId ?? '')
  const archiveClient = useArchiveClient()
  const updateClient = useUpdateClient()
  const hcpStatus = useHousecallProStatus()

  const [actionError, setActionError] = React.useState<string | null>(null)
  const [editingDetails, setEditingDetails] = React.useState<boolean>(false)

  if (clientId !== null && client.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
      </div>
    )
  }

  if (clientId === null || client.isError || !client.data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="rounded-lg border border-default bg-surface px-3 py-12 text-center">
          <p className="text-sm font-medium">Client not found</p>
          <p className="mt-1 text-sm text-muted-text">
            It may have been removed, or the link may be wrong.
          </p>
        </div>
      </div>
    )
  }

  const data = client.data
  // A closure does not keep the narrowing from the returns above.
  const id = clientId
  const isArchived = data.archivedAt !== null
  // Housecall Pro links only where HCP is connected: elsewhere there is
  // nothing to search or link to.
  const hcpWrites =
    hcpStatus.data?.configured === true ? hcpStatus.data.writesEnabled : null

  async function handleArchiveToggle(): Promise<void> {
    setActionError(null)
    try {
      await archiveClient.mutateAsync({ id, archived: !isArchived })
    } catch (caught) {
      setActionError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  /**
   * Moving a client from Prospect to Active is the moment work is won, so it is
   * a single click on the client itself rather than a trip through an edit form.
   */
  async function handleStatusChange(status: ClientStatus): Promise<void> {
    setActionError(null)
    try {
      await updateClient.mutateAsync({ id, input: { status } })
    } catch (caught) {
      setActionError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  return (
    <div className="space-y-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">{data.name}</h1>
            <ClientTypeBadge type={data.type} />
            <ClientStatusBadge status={data.status} />
            {isArchived ? (
              <span className="text-xs text-muted-text">Archived</span>
            ) : null}
          </div>
          <p className="text-sm text-muted-text">
            {formatAddress(data)}
            {data.ownerName ? ` · Owner: ${data.ownerName}` : ''}
          </p>
          {hcpWrites === null ? null : (
            <HcpCustomerLink client={data} writesEnabled={hcpWrites} />
          )}
        </div>
        <div className="flex items-start gap-2">
          {/*
            Associations only, or any client that already has a figure: a
            homeowner is one home, and asking makes the header noisier for
            the clients who would never fill it in.
          */}
          {data.type === 'COMMERCIAL' || data.homeCount !== null ? (
            <HomeCountControl clientId={clientId} homeCount={data.homeCount} />
          ) : null}
          <label className="flex items-center gap-1.5 text-xs text-muted-text">
            Status
            <select
              className={INLINE_SELECT_CLASS}
              value={data.status}
              disabled={updateClient.isPending}
              onChange={(event) => {
                void handleStatusChange(
                  toClientStatus(event.target.value, data.status),
                )
              }}
            >
              {CLIENT_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {CLIENT_STATUS_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          {editingDetails ? null : (
            <Button
              variant="outline"
              onClick={() => {
                setEditingDetails(true)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit details
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleArchiveToggle}
            disabled={archiveClient.isPending}
          >
            {isArchived ? (
              <RotateCcw className="h-3.5 w-3.5" />
            ) : (
              <Archive className="h-3.5 w-3.5" />
            )}
            {isArchived ? 'Restore' : 'Archive'}
          </Button>
        </div>
      </header>

      {actionError ? (
        <p className="text-sm text-destructive">{actionError}</p>
      ) : null}

      {editingDetails ? (
        <ClientDetailsForm
          client={data}
          onDone={() => {
            setEditingDetails(false)
          }}
        />
      ) : null}

      {data.notes && !editingDetails ? (
        <section className="rounded-lg border border-default bg-surface p-4">
          <h2 className="text-sm font-medium text-secondary-text">Notes</h2>
          <p className="mt-1 text-sm whitespace-pre-wrap">{data.notes}</p>
        </section>
      ) : null}

      <ContactsSection clientId={clientId} contacts={data.contacts} />
      <SitesSection
        clientId={clientId}
        sites={data.sites}
        hcp={
          hcpWrites === null || data.hcpCustomer === null
            ? null
            : { customer: data.hcpCustomer, writesEnabled: hcpWrites }
        }
      />
      <ProposalsSection clientId={clientId} />
      <DocumentsSection clientId={clientId} sites={data.sites} />
    </div>
  )
}

/** Narrow a `<select>` value back to a client type. */
function toClientType(value: string, fallback: ClientType): ClientType {
  return CLIENT_TYPES.find((option) => option === value) ?? fallback
}

/**
 * The client's own details, editable after it is created.
 *
 * The name prints on every proposal's cover, so a misspelling found later has
 * to be fixable without recreating the client and its history. Homes and
 * status are edited in the header, where they are already one click.
 */
function ClientDetailsForm({
  client,
  onDone,
}: {
  client: ClientDetailDto
  onDone: () => void
}) {
  const updateClient = useUpdateClient()
  const [name, setName] = React.useState<string>(client.name)
  const [type, setType] = React.useState<ClientType>(client.type)
  const [addressLine1, setAddressLine1] = React.useState<string>(
    client.addressLine1 ?? '',
  )
  const [city, setCity] = React.useState<string>(client.city ?? '')
  const [state, setState] = React.useState<string>(client.state ?? '')
  const [postalCode, setPostalCode] = React.useState<string>(
    client.postalCode ?? '',
  )
  const [notes, setNotes] = React.useState<string>(client.notes ?? '')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = updateClientInputSchema.safeParse({
      name,
      type,
      addressLine1,
      city,
      state,
      postalCode,
      notes,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the details')
      return
    }
    try {
      await updateClient.mutateAsync({ id: client.id, input: parsed.data })
      onDone()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  return (
    <form
      className="space-y-3 rounded-lg border border-default bg-surface p-4"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-1.5">
          <Label htmlFor="client-name">Name</Label>
          <Input
            id="client-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-type">Type</Label>
          <select
            id="client-type"
            className="h-8 w-full rounded-md border border-default bg-surface px-2 text-sm text-primary-text transition-colors hover:border-strong focus-visible:outline-none"
            value={type}
            onChange={(event) => {
              setType(toClientType(event.target.value, type))
            }}
          >
            {CLIENT_TYPES.map((option) => (
              <option key={option} value={option}>
                {CLIENT_TYPE_LABELS[option]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="client-address">Billing address</Label>
        <Input
          id="client-address"
          value={addressLine1}
          placeholder="1234 Kewanee Ave"
          onChange={(event) => setAddressLine1(event.target.value)}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="client-city">City</Label>
          <Input
            id="client-city"
            value={city}
            onChange={(event) => setCity(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-state">State</Label>
          <Input
            id="client-state"
            value={state}
            onChange={(event) => setState(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-zip">ZIP</Label>
          <Input
            id="client-zip"
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="client-notes">Notes</Label>
        <textarea
          id="client-notes"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          className="flex w-full rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none"
        />
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={updateClient.isPending}>
          {updateClient.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save details
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function BackLink() {
  return (
    <Link
      to="/clients"
      className="inline-flex items-center gap-1.5 text-sm text-muted-text hover:text-primary-text"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Clients
    </Link>
  )
}

interface SectionProps {
  title: string
  count: number
  isAdding: boolean
  onAdd: () => void
  addLabel: string
  children: React.ReactNode
}

/** Shared section chrome so contacts and sites look identical. */
function Section({
  title,
  count,
  isAdding,
  onAdd,
  addLabel,
  children,
}: SectionProps) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {title}{' '}
          <span className="text-sm font-normal text-muted-text">{count}</span>
        </h2>
        {isAdding ? null : (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-3.5 w-3.5" />
            {addLabel}
          </Button>
        )}
      </div>
      {children}
    </section>
  )
}

interface ContactsSectionProps {
  clientId: string
  contacts: ContactDto[]
}

function ContactsSection({ clientId, contacts }: ContactsSectionProps) {
  const addContact = useAddContact()
  const updateContact = useUpdateContact()
  const archiveContact = useArchiveContact()

  const [isAdding, setIsAdding] = React.useState<boolean>(false)
  // The contact being edited, which reuses the add form. A proposal is sent to
  // the primary contact, so a mistyped address has to be fixable here.
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [name, setName] = React.useState<string>('')
  const [title, setTitle] = React.useState<string>('')
  const [email, setEmail] = React.useState<string>('')
  const [phone, setPhone] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)

  const isFormOpen = isAdding || editingId !== null

  function resetForm(): void {
    setName('')
    setTitle('')
    setEmail('')
    setPhone('')
    setError(null)
  }

  function closeForm(): void {
    resetForm()
    setIsAdding(false)
    setEditingId(null)
  }

  function startEditing(contact: ContactDto): void {
    setIsAdding(false)
    setEditingId(contact.id)
    setName(contact.name)
    setTitle(contact.title ?? '')
    setEmail(contact.email ?? '')
    setPhone(contact.phone ?? '')
    setError(null)
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    if (editingId === null) {
      await handleAdd(event)
      return
    }
    event.preventDefault()
    setError(null)
    const parsed = updateContactInputSchema.safeParse({
      name,
      title,
      email,
      phone,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the contact details')
      return
    }
    try {
      await updateContact.mutateAsync({
        id: editingId,
        clientId,
        input: parsed.data,
      })
      closeForm()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleAdd(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setError(null)

    const parsed = createContactInputSchema.safeParse({
      name,
      title,
      email,
      phone,
      isPrimary: contacts.filter((c) => !c.archivedAt).length === 0,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the contact details')
      return
    }

    try {
      await addContact.mutateAsync({ clientId, input: parsed.data })
      resetForm()
      setIsAdding(false)
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleMakePrimary(contact: ContactDto): Promise<void> {
    setError(null)
    try {
      await updateContact.mutateAsync({
        id: contact.id,
        clientId,
        input: { isPrimary: true },
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleArchiveToggle(contact: ContactDto): Promise<void> {
    setError(null)
    try {
      await archiveContact.mutateAsync({
        id: contact.id,
        clientId,
        archived: contact.archivedAt === null,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  return (
    <Section
      title="Contacts"
      count={contacts.filter((c) => !c.archivedAt).length}
      isAdding={isFormOpen}
      onAdd={() => {
        resetForm()
        setEditingId(null)
        setIsAdding(true)
      }}
      addLabel="Add contact"
    >
      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {isFormOpen ? (
        <form
          className="rounded-lg border border-default bg-surface p-3"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                value={name}
                placeholder="Donnie Evans"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-title">Role</Label>
              <Input
                id="contact-title"
                value={title}
                placeholder="Board President"
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={phone}
                placeholder="(806) 555-0142"
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={addContact.isPending || updateContact.isPending}
            >
              {addContact.isPending || updateContact.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              {editingId === null ? 'Add contact' : 'Save contact'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={closeForm}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {contacts.length === 0 ? (
        <EmptyRow icon={<UserRound className="mx-auto h-5 w-5 text-disabled-text" />}>
          No contacts yet. Add the person who signs off on work.
        </EmptyRow>
      ) : (
        <div className={TABLE_FRAME}>
          <table className={TABLE}>
            <thead>
              <tr className="border-b border-default text-secondary-text">
                <th className={cn(TH, COL_STICKY, 'bg-surface')}>Name</th>
                <th className={TH}>Role</th>
                <th className={cn(TH, COL_SECONDARY)}>Email</th>
                <th className={cn(TH, COL_OPTIONAL)}>Phone</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr
                  key={contact.id}
                  className={cn(
                    ROW,
                    'group border-b border-default last:border-b-0',
                    contact.archivedAt ? 'opacity-55' : null,
                  )}
                >
                  <td className={cn(TD, COL_STICKY)}>
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      {contact.isPrimary ? (
                        <Star
                          className="h-3.5 w-3.5 fill-current text-warning"
                          aria-label="Primary contact"
                        />
                      ) : null}
                      {contact.name}
                    </span>
                    {contact.archivedAt ? (
                      <span className="ml-2 text-xs text-muted-text">
                        Archived
                      </span>
                    ) : null}
                  </td>
                  <td className={cn(TD, 'text-secondary-text')}>
                    {contact.title ?? '—'}
                  </td>
                  <td className={cn(TD, COL_SECONDARY, 'text-secondary-text')}>
                    {contact.email ?? '—'}
                  </td>
                  <td className={cn(TD, COL_OPTIONAL, 'text-secondary-text')}>
                    {contact.phone ?? '—'}
                  </td>
                  <td className={cn(TD, 'text-right')}>
                    <span className={cn('inline-flex gap-1', ROW_ACTION)}>
                      {contact.archivedAt ? null : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            startEditing(contact)
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                      )}
                      {!contact.isPrimary && !contact.archivedAt ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMakePrimary(contact)}
                        >
                          Make primary
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleArchiveToggle(contact)}
                      >
                        {contact.archivedAt ? 'Restore' : 'Archive'}
                      </Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

interface SitesSectionProps {
  clientId: string
  sites: SiteDto[]
  /**
   * The client's Housecall Pro customer, when HCP is connected and the client
   * is linked: each site then chooses its address on it. Null hides the
   * column, since there is nothing to choose among.
   */
  hcp: {
    customer: NonNullable<ClientDetailDto['hcpCustomer']>
    writesEnabled: boolean
  } | null
}

/** The fields the edit form holds, as text. */
interface SiteDetailsDraft {
  name: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
}

function toSiteDraft(site: SiteDto): SiteDetailsDraft {
  return {
    name: site.name,
    addressLine1: site.addressLine1 ?? '',
    addressLine2: site.addressLine2 ?? '',
    city: site.city ?? '',
    state: site.state ?? '',
    postalCode: site.postalCode ?? '',
  }
}

/** Blank means "not recorded", which is null rather than an empty string. */
function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function SitesSection({ clientId, sites, hcp }: SitesSectionProps) {
  const addSite = useAddSite()
  const archiveSite = useArchiveSite()
  const updateSite = useUpdateSite()

  const [isAdding, setIsAdding] = React.useState<boolean>(false)
  const [name, setName] = React.useState<string>('')
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  function resetForm(): void {
    setName('')
    setError(null)
  }

  /**
   * Adding a site asks for a name and nothing else.
   *
   * Everything else is learned later and from somewhere better: the acreage
   * from the boundary once it is drawn, the map status from what has actually
   * been done, the address whenever somebody has it. Asking for all of it up
   * front made creating a site a form to fill in, at the one moment when the
   * only thing known for certain is what the place is called.
   */
  async function handleAdd(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setError(null)

    const parsed = createSiteInputSchema.safeParse({ name })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the site details')
      return
    }

    try {
      await addSite.mutateAsync({ clientId, input: parsed.data })
      resetForm()
      setIsAdding(false)
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleSaveDetails(
    site: SiteDto,
    draft: SiteDetailsDraft,
  ): Promise<void> {
    setError(null)
    const parsed = updateSiteInputSchema.safeParse({
      name: draft.name,
      addressLine1: orNull(draft.addressLine1),
      addressLine2: orNull(draft.addressLine2),
      city: orNull(draft.city),
      state: orNull(draft.state),
      postalCode: orNull(draft.postalCode),
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the site details')
      return
    }

    try {
      await updateSite.mutateAsync({
        id: site.id,
        clientId,
        input: parsed.data,
      })
      setEditingId(null)
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleArchiveToggle(site: SiteDto): Promise<void> {
    setError(null)
    try {
      await archiveSite.mutateAsync({
        id: site.id,
        clientId,
        archived: site.archivedAt === null,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  /**
   * Map progress changes often and on its own schedule, so it is editable right
   * in the row — no edit form for a one-field change.
   */
  async function handleMapStatusChange(
    site: SiteDto,
    mapStatus: SiteMapStatus,
  ): Promise<void> {
    setError(null)
    try {
      await updateSite.mutateAsync({
        id: site.id,
        clientId,
        input: { mapStatus },
      })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  const editing = sites.find((site) => site.id === editingId) ?? null

  return (
    <Section
      title="Sites"
      count={sites.filter((s) => !s.archivedAt).length}
      isAdding={isAdding}
      onAdd={() => setIsAdding(true)}
      addLabel="Add site"
    >
      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {isAdding ? (
        <form
          className="rounded-lg border border-default bg-surface p-3"
          onSubmit={handleAdd}
          noValidate
        >
          <div className="max-w-md space-y-1.5">
            <Label htmlFor="site-name">Site name</Label>
            <Input
              id="site-name"
              value={name}
              placeholder="Adrianne K Hamilton Park"
              onChange={(event) => setName(event.target.value)}
            />
            <p className="text-xs text-muted-text">
              The address can be added later. Acreage comes from the boundary
              once it is drawn.
            </p>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button type="submit" size="sm" disabled={addSite.isPending}>
              {addSite.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Add site
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                resetForm()
                setIsAdding(false)
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {sites.length === 0 ? (
        <EmptyRow icon={<MapPin className="mx-auto h-5 w-5 text-disabled-text" />}>
          No sites yet. A client can have several, such as one per park.
        </EmptyRow>
      ) : (
        <div className="space-y-2">
          <div className={TABLE_FRAME}>
            <table className={TABLE}>
              <thead>
                <tr className="border-b border-default text-secondary-text">
                  <th className={cn(TH, COL_STICKY, 'bg-surface')}>Site</th>
                  <th className={TH}>Map</th>
                  <th className={cn(TH, COL_SECONDARY)}>Last visit</th>
                  <th className={cn(TH, COL_SECONDARY)}>Location</th>
                  <th className={cn(TH_RIGHT, COL_OPTIONAL)}>Acres</th>
                  {hcp === null ? null : (
                    <th className={cn(TH, COL_SECONDARY)}>Housecall Pro</th>
                  )}
                  <th className={TH} />
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr
                    key={site.id}
                    className={cn(
                      ROW,
                      'group border-b border-default last:border-b-0',
                      site.archivedAt ? 'opacity-55' : null,
                    )}
                  >
                    <td className={cn(TD, COL_STICKY, 'font-medium')}>
                      {site.name}
                      {site.archivedAt ? (
                        <span className="ml-2 text-xs text-muted-text">
                          Archived
                        </span>
                      ) : null}
                    </td>
                    <td className={TD}>
                      <MapStatusCell
                        site={site}
                        isSaving={updateSite.isPending}
                        onChange={(status) => {
                          void handleMapStatusChange(site, status)
                        }}
                      />
                    </td>
                    <td className={cn(TD, COL_SECONDARY)}>
                      <SiteVisitsCell siteId={site.id} siteName={site.name} />
                    </td>
                    <td className={cn(TD, COL_SECONDARY, 'text-secondary-text')}>
                      {formatAddress(site)}
                    </td>
                    <td
                      className={cn(
                        TD_RIGHT,
                        COL_OPTIONAL,
                        'tabular-nums text-secondary-text',
                      )}
                    >
                      {/*
                        Empty until a boundary exists, because the figure IS the
                        boundary's area. A dash reads as "not mapped yet" rather
                        than as a park with no size.
                      */}
                      {site.acreage ?? '—'}
                    </td>
                    {hcp === null ? null : (
                      <td className={cn(TD, COL_SECONDARY)}>
                        {site.archivedAt === null ? (
                          <HcpAddressLink
                            site={site}
                            customer={hcp.customer}
                            writesEnabled={hcp.writesEnabled}
                          />
                        ) : null}
                      </td>
                    )}
                    <td className={cn(TD, 'text-right')}>
                      <SiteRowActions
                        site={site}
                        onEdit={() => {
                          setError(null)
                          setEditingId(site.id)
                        }}
                        onArchiveToggle={() => {
                          void handleArchiveToggle(site)
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            The edit form sits below the table rather than replacing a row. Six
            fields do not fit in a row that is already scrolling sideways on a
            phone, and a row that grows to hold them pushes every site under it
            off the screen.
          */}
          {editing === null ? null : (
            <SiteDetailsForm
              // Keyed by site so switching rows remounts the form with that
              // site's values, rather than leaving the previous one's in it.
              key={editing.id}
              site={editing}
              isSaving={updateSite.isPending}
              onCancel={() => setEditingId(null)}
              onSave={(draft) => {
                void handleSaveDetails(editing, draft)
              }}
            />
          )}
        </div>
      )}
    </Section>
  )
}

/**
 * The row's actions, with the emphasis following what the site actually needs.
 *
 * Without a boundary, **Boundary** is the prominent one: an outline is what
 * makes the survey screen useful — it is what flags a tree as off the property,
 * and it is what the acreage is measured from — and tracing it is a desk job
 * done on aerial imagery, ideally before anyone drives out.
 *
 * Trees stays clickable throughout, deliberately. Gating it would mean someone
 * standing under a tree with a phone, discovering nobody traced the outline,
 * and being unable to do the job they drove there for. The trees they record
 * are perfectly good without one, and the boundary warnings appear over them
 * retroactively the moment it is drawn.
 */
function SiteRowActions({
  site,
  onEdit,
  onArchiveToggle,
}: {
  site: SiteDto
  onEdit: () => void
  onArchiveToggle: () => void
}) {
  if (site.archivedAt !== null) {
    return (
      <Button
        size="sm"
        variant="ghost"
        className={ROW_ACTION}
        onClick={onArchiveToggle}
      >
        Restore
      </Button>
    )
  }

  const hasBoundary = site.boundary !== null

  // One flex row, as the contacts table does: loose in the cell, each button
  // sat on its own baseline, and Archive, with no icon, sat lower than the rest.
  return (
    <span className="inline-flex gap-1">
      <Button size="sm" variant="ghost" className={ROW_ACTION} onClick={onEdit}>
        <Pencil className="h-3.5 w-3.5" />
        Edit
      </Button>
      <Button
        size="sm"
        variant={hasBoundary ? 'ghost' : 'outline'}
        className={hasBoundary ? ROW_ACTION : undefined}
        asChild
      >
        <Link to={`/sites/${site.id}/boundary`}>
          <Spline className="h-3.5 w-3.5" />
          Boundary
        </Link>
      </Button>
      <Button
        size="sm"
        variant={hasBoundary ? 'outline' : 'ghost'}
        className={hasBoundary ? undefined : ROW_ACTION}
        asChild
      >
        <Link to={`/sites/${site.id}/survey`}>
          <TreePine className="h-3.5 w-3.5" />
          Trees
        </Link>
      </Button>
      <Button size="sm" variant="ghost" className={ROW_ACTION} asChild>
        <Link to={`/sites/${site.id}/irrigation`}>
          <Droplet className="h-3.5 w-3.5" />
          Irrigation
        </Link>
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className={ROW_ACTION}
        onClick={onArchiveToggle}
      >
        Archive
      </Button>
    </span>
  )
}

/**
 * Editing a site's name and address.
 *
 * Deliberately has nothing to do with maps. The outline is drawn on its own
 * screen and the acreage follows from it; what is left here is the handful of
 * facts somebody types. It exists because before it there was no way to record
 * an address at all except by getting it right the moment the site was created.
 */
function SiteDetailsForm({
  site,
  isSaving,
  onCancel,
  onSave,
}: {
  site: SiteDto
  isSaving: boolean
  onCancel: () => void
  onSave: (draft: SiteDetailsDraft) => void
}) {
  const [draft, setDraft] = React.useState<SiteDetailsDraft>(() =>
    toSiteDraft(site),
  )

  function set<K extends keyof SiteDetailsDraft>(field: K, value: string): void {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  return (
    <form
      className="rounded-lg border border-strong bg-surface p-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        onSave(draft)
      }}
    >
      <p className="mb-3 text-sm font-medium">Edit {site.name}</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="edit-site-name">Site name</Label>
          <Input
            id="edit-site-name"
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="edit-site-address1">Address</Label>
          <Input
            id="edit-site-address1"
            value={draft.addressLine1}
            placeholder="4400 Regal Park Dr"
            onChange={(event) => set('addressLine1', event.target.value)}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="edit-site-address2">
            Address line 2{' '}
            <span className="font-normal text-muted-text">(optional)</span>
          </Label>
          <Input
            id="edit-site-address2"
            value={draft.addressLine2}
            onChange={(event) => set('addressLine2', event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-site-city">City</Label>
          <Input
            id="edit-site-city"
            value={draft.city}
            placeholder="Lubbock"
            onChange={(event) => set('city', event.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-site-state">State</Label>
            <Input
              id="edit-site-state"
              value={draft.state}
              placeholder="TX"
              onChange={(event) => set('state', event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-site-postal">ZIP</Label>
            <Input
              id="edit-site-postal"
              value={draft.postalCode}
              placeholder="79424"
              onChange={(event) => set('postalCode', event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button type="submit" size="sm" disabled={isSaving}>
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Save changes
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/**
 * Narrow a select's string value back to its enum without an unsafe cast.
 *
 * `<select>` hands back a bare `string`; asserting it into the union would be
 * exactly the lie `swocrm/no-unsafe-type-assertion` exists to prevent. Matching
 * against the shared enum's own values means an option the API would reject can
 * never leave this component.
 */
function toMapStatus(value: string): SiteMapStatus {
  const match = SITE_MAP_STATUSES.find((option) => option === value)
  return match ?? 'NONE'
}

function toClientStatus(value: string, fallback: ClientStatus): ClientStatus {
  const match = CLIENT_STATUSES.find((option) => option === value)
  return match ?? fallback
}

/** Compact inline select, sized to sit inside a table row without stretching it. */
const INLINE_SELECT_CLASS =
  'h-7 rounded-md border border-default bg-surface px-1.5 text-xs text-primary-text transition-colors hover:border-strong focus-visible:outline-none disabled:opacity-60'

interface ProposalsSectionProps {
  clientId: string
}

/**
 * The client's proposals. Creating one is a route rather than an inline form:
 * a proposal needs a title, a description, and a site selection, which is more
 * than belongs in a strip inside another page.
 */
function ProposalsSection({ clientId }: ProposalsSectionProps) {
  const navigate = useNavigate()
  const proposals = useProposals({ clientId })

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Proposals{' '}
          {proposals.data ? (
            <span className="text-sm font-normal text-muted-text">
              {proposals.data.total}
            </span>
          ) : null}
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/clients/${clientId}/proposals/new`)}
        >
          <FileSignature className="h-3.5 w-3.5" />
          New proposal
        </Button>
      </div>
      <ProposalTable
        isPending={proposals.isPending}
        isError={proposals.isError}
        proposals={proposals.data?.proposals ?? []}
        hasSearch={false}
        showClient={false}
      />
    </section>
  )
}

interface DocumentsSectionProps {
  clientId: string
  sites: SiteDto[]
}

/**
 * Every file for this client — maps attached to its sites plus anything filed
 * against the client itself, which is what the API's client-scoped query
 * returns as a union.
 *
 * Uploading asks which site the file belongs to, because a site map without a
 * site is just a PDF. When the client has no sites the file is filed against
 * the client instead, so the upload is never blocked.
 *
 * Closed until asked for. The list includes every photo from the survey, so
 * on a surveyed client it is the longest thing on the page and the least
 * often needed; the count beside the heading says what is in it.
 */
function DocumentsSection({ clientId, sites }: DocumentsSectionProps) {
  const documents = useDocuments({ clientId })
  const liveSites = sites.filter((site) => site.archivedAt === null)
  const [targetSiteId, setTargetSiteId] = React.useState<string>('')
  const [open, setOpen] = React.useState<boolean>(false)

  const resolvedSiteId =
    targetSiteId !== '' && liveSites.some((site) => site.id === targetSiteId)
      ? targetSiteId
      : (liveSites[0]?.id ?? '')

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => {
              setOpen((current) => !current)
            }}
            className="flex items-center gap-1.5"
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 text-muted-text transition-transform',
                open ? 'rotate-90' : null,
              )}
            />
            Files{' '}
            {documents.data ? (
              <span className="text-sm font-normal text-muted-text">
                {documents.data.total}
              </span>
            ) : null}
          </button>
        </h2>
        <div className={cn('flex items-center gap-2', open ? null : 'hidden')}>
          {liveSites.length > 0 ? (
            <label className="flex items-center gap-1.5 text-xs text-muted-text">
              Attach to
              <select
                className={INLINE_SELECT_CLASS}
                value={resolvedSiteId}
                onChange={(event) => setTargetSiteId(event.target.value)}
              >
                {liveSites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <UploadButton
            label="Upload file"
            meta={
              resolvedSiteId !== ''
                ? { kind: 'SITE_MAP', siteId: resolvedSiteId }
                : { kind: 'OTHER', clientId }
            }
          />
        </div>
      </div>
      {open ? (
        <DocumentTable
          isPending={documents.isPending}
          isError={documents.isError}
          documents={documents.data?.documents ?? []}
          emptyMessage={
            liveSites.length > 0
              ? 'No files yet. Upload a site map, or survey a site to add its photos.'
              : 'No files yet. Add a site first if you want to attach a map to it.'
          }
        />
      ) : null}
    </section>
  )
}

interface MapStatusCellProps {
  site: SiteDto
  isSaving: boolean
  onChange: (status: SiteMapStatus) => void
}

/**
 * Map status: a colored pill at rest, a select once clicked.
 *
 * This is the design system's direct-manipulation rule — click the cell to edit
 * it, no form — while keeping the semantic color that makes the column
 * scannable. An always-visible select would have traded that color away, and
 * "which sites still need a map" is exactly the question this column answers at
 * a glance.
 */
function MapStatusCell({ site, isSaving, onChange }: MapStatusCellProps) {
  const [isEditing, setIsEditing] = React.useState<boolean>(false)

  // A drawn boundary settles it (the API reports such a site as mapped), so
  // offering a choice there would be offering one that cannot take effect.
  if (site.archivedAt !== null || site.boundary !== null) {
    return <SiteMapStatusBadge status={site.mapStatus} />
  }

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        aria-label={`Change map status for ${site.name}`}
        className="rounded-full transition-opacity hover:opacity-80"
      >
        <SiteMapStatusBadge status={site.mapStatus} />
      </button>
    )
  }

  return (
    <select
      // Focus follows the click: this select replaces the button the user just
      // activated, so without it a keyboard user is dropped back to the top of
      // the document. Safe here precisely because it is a response to an
      // explicit action, not focus stolen on page load.
      autoFocus
      aria-label={`Map status for ${site.name}`}
      className={INLINE_SELECT_CLASS}
      value={site.mapStatus}
      disabled={isSaving}
      onBlur={() => setIsEditing(false)}
      onChange={(event) => {
        onChange(toMapStatus(event.target.value))
        setIsEditing(false)
      }}
    >
      {SITE_MAP_STATUSES.map((option) => (
        <option key={option} value={option}>
          {SITE_MAP_STATUS_LABELS[option]}
        </option>
      ))}
    </select>
  )
}

interface EmptyRowProps {
  icon: React.ReactNode
  children: React.ReactNode
}

function EmptyRow({ icon, children }: EmptyRowProps) {
  return (
    <div className="rounded-lg border border-default bg-surface px-3 py-8 text-center">
      {icon}
      <p className="mt-2 text-sm text-muted-text">{children}</p>
    </div>
  )
}
